/* eslint-disable no-else-return */
import React, {
  useState,
  useRef,
  RefObject,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { setSelectedCallbackProspect } from '../../../../../../../../../dialer-slice';
import { CallLog } from '../../../../../../../../../types/call-logs';
import Switch, {
  Size as SwitchSize,
} from '../../../../../../../../../../../shared/design-system/components/atoms/switch';
import RadioButtonGroup from '../../../../../../../../../../../shared/design-system/components/radio-button-group';
import PhoneNumberSearchInputV2, {
  PhoneNumberOption,
} from '../../../../../../../../../../../shared/components/phone-number-search-input/PhoneNumberSearchInputV2';
import { searchMemberPhoneNumbers } from '../../../../../../../../../helpers/dialer.api';
import Input from '../../../../../../../../../../../shared/design-system/components/input';
import Button from '../../../../../../../../../../../shared/design-system/ui/button';
import {
  GetPhoneNumberSettingsResponse,
  PhoneNumberSettingCode,
} from '../../../../../../../../../types/phone-numbers';
import { RequestStatus } from '../../../../../../../../../../../shared/enums/request-status';
import {
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../../../../../../shared/utils';
import { parsePhoneNumber } from 'libphonenumber-js';

const FORWARDING_NUMBER_MIN_DIGITS = 5;
const FORWARDING_NUMBER_MAX_DIGITS = 15;

const getForwardingNumberDigitLength = (
  num: string | null | undefined,
): number => (num ?? '').replace(/\D/g, '').length;

/** Empty field returns '' (no length error); invalid length returns a message. */
const getForwardingNumberLengthErrorMessage = (
  num: string | null | undefined,
): string => {
  if (num == null || String(num).trim() === '') return '';
  const len = getForwardingNumberDigitLength(num);
  if (
    len < FORWARDING_NUMBER_MIN_DIGITS ||
    len > FORWARDING_NUMBER_MAX_DIGITS
  ) {
    return 'Phone number must be between 5 and 15 digits';
  }
  return '';
};

const getForwardingPhoneInputErrorMessage = (options: {
  isSameAsLineNumber: boolean;
  isFieldActive: boolean;
  value: string | null | undefined;
}): string => {
  const { isSameAsLineNumber, isFieldActive, value } = options;
  if (!isFieldActive) {
    return '';
  }
  if (isSameAsLineNumber) {
    return 'Forwarding to same number not allowed';
  }
  return getForwardingNumberLengthErrorMessage(value);
};

export type ForwardingType = 'always' | 'conditional';

type ForwardingMode = 'none' | 'always' | 'conditional';

export type TimeSlotItem = {
  hour: number | string;
  minute: number | string;
};

export type TimeRangeValue = {
  start: TimeSlotItem;
  end: TimeSlotItem;
};

export interface ICallForwardingSettingsContentProps {
  settingsMap: Record<PhoneNumberSettingCode, string | null>;
  activeNumberId?: number;
  updatePhoneNumberSettingsRequestStatus: RequestStatus;
  onSaveForwardingConfiguration: (
    settings: Array<{ code: string; value: string | null }>,
  ) => void;
  // Modal related props
  isModal?: boolean;
  passIsForwardingConfigurationValid?: (isValid: boolean) => void;
  setIsForwardingEnabledForModal?: (isForwardingEnabled: boolean) => void;
  setSettingsToApplyForModal?: (
    settings: Array<{ code: string; value: string | null }>,
  ) => void;
  activeNumberSettings: GetPhoneNumberSettingsResponse | null;
  resetGetPhoneNumberDetailsRequest: () => void;
  getPhoneNumberDetailsRequestStatus: RequestStatus;
}

const CallForwardingSettingsContent: React.FC<ICallForwardingSettingsContentProps> = ({
  activeNumberSettings,
  settingsMap,
  activeNumberId,
  updatePhoneNumberSettingsRequestStatus,
  onSaveForwardingConfiguration,
  isModal = false,
  passIsForwardingConfigurationValid,
  setIsForwardingEnabledForModal,
  setSettingsToApplyForModal,
  resetGetPhoneNumberDetailsRequest,
  getPhoneNumberDetailsRequestStatus,
}) => {
  const dispatch = useDispatch();

  const isValidForwardingNumber = useCallback((num: string | null): boolean => {
    if (!num) return false;
    const len = getForwardingNumberDigitLength(num);
    return (
      len >= FORWARDING_NUMBER_MIN_DIGITS && len <= FORWARDING_NUMBER_MAX_DIGITS
    );
  }, []);

  const handleTestNumber = (number: string) => {
    let callingCode = null;
    let phoneNumber = null;
    let countryISOCode = null;

    try {
      const parsed = parsePhoneNumber(number);
      if (parsed) {
        callingCode = parsed.countryCallingCode?.toString() || '';
        phoneNumber = parsed.nationalNumber?.toString() || number;
        countryISOCode = parsed.country;
      }
    } catch {
      // If parsing fails, use the full number without calling code
    }
    dispatch(
      setSelectedCallbackProspect([
        {
          prospectPhone: {
            callingCode,
            number,
            phoneNumber,
            countryISOCode,
          },
        } as CallLog,
      ]),
    );
  };

  const buildTestLinkHandler = (
    phoneNumber: string | null,
    disabled: boolean,
  ) => {
    if (disabled || !phoneNumber) return undefined;
    return () => handleTestNumber(phoneNumber);
  };

  const buildTestLinkKeyDownHandler = (
    phoneNumber: string | null,
    disabled: boolean,
  ) => {
    if (disabled || !phoneNumber) return undefined;
    return (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleTestNumber(phoneNumber);
      }
    };
  };

  const forwardingEnabled = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_ENABLED]
    : null;
  const forwardingAlwaysEnabled = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_ALWAYS_ENABLED]
    : null;
  const forwardingAlwaysNumber = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_ALWAYS_NUMBER]
    : null;
  const forwardingUnansweredEnabled = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_UNANSWERED_ENABLED]
    : null;
  const forwardingUnansweredSeconds = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_UNANSWERED_SECONDS]
    : null;
  const forwardingUnansweredNumber = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_UNANSWERED_NUMBER]
    : null;
  const forwardingBusyEnabled = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_BUSY_ENABLED]
    : null;
  const forwardingBusyNumber = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_BUSY_NUMBER]
    : null;
  const forwardingOutOfOfficeEnabled = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_OOF_ENABLED]
    : null;
  const forwardingOutOfOfficeNumber = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_OOF_NUMBER]
    : null;
  const forwardingOOOStart = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_OOF_START]
    : null;
  const forwardingOOOEnd = settingsMap
    ? settingsMap[PhoneNumberSettingCode.FORWARDING_OOF_END]
    : null;

  const [isForwardingEnabled, setIsForwardingEnabled] = useState(true);
  const [forwardingType, setForwardingType] = useState<ForwardingType>(
    'always',
  );
  const [isUnansweredEnabled, setIsUnansweredEnabled] = useState(false);
  const [isBusyEnabled, setIsBusyEnabled] = useState(false);
  const [isOutOfOfficeEnabled, setIsOutOfOfficeEnabled] = useState(false);
  const [unansweredAfterSeconds, setUnansweredAfterSeconds] = useState<string>(
    '20',
  );
  const [outOfOfficeHours, setOutOfOfficeHours] = useState<TimeRangeValue>({
    start: { hour: '', minute: '' },
    end: { hour: '', minute: '' },
  });
  const [forwardingNumber, setForwardingNumber] = useState({
    always: forwardingAlwaysNumber,
    unanswered: forwardingUnansweredNumber,
    busy: forwardingBusyNumber,
    ooo: forwardingOutOfOfficeNumber,
  });
  const [oooTimeValid, setOooTimeValid] = useState<boolean>(true);
  const [errors, setErrors] = useState<string>(null);

  const inputStartHourRef = useRef<HTMLInputElement>(null);
  const inputStartMinRef = useRef<HTMLInputElement>(null);
  const inputEndHourRef = useRef<HTMLInputElement>(null);
  const inputEndMinRef = useRef<HTMLInputElement>(null);
  const memberPhoneNumbersCacheRef = useRef<PhoneNumberOption[] | null>(null);

  const loadPhoneNumberOptions = useCallback(async (search: string): Promise<
    PhoneNumberOption[]
  > => {
    try {
      const response = await searchMemberPhoneNumbers({
        search: search || '',
        pageNumber: 1,
        pageSize: 25,
      });
      const memberPhoneNumbers = response?.payload?.memberPhoneNumbers || [];
      if (!Array.isArray(memberPhoneNumbers)) return [];
      return memberPhoneNumbers.map((item) => ({
        value: item?.phoneNumber,
        label: item?.teamMember
          ? `${item.teamMember.firstName} ${item.teamMember.lastName} ${item.phoneNumber}`
          : item.phoneNumber,
        phoneNumber: item?.phoneNumber,
        teamMember: item?.teamMember || null,
        id: item?.id,
      }));
    } catch {
      return [];
    }
  }, []);

  const isConditionalForwardingEnabled = forwardingType === 'conditional';
  const forwardingMode: ForwardingMode = !isForwardingEnabled ? 'none' : forwardingType;

  const handleForwardingModeChange = (mode: ForwardingMode) => {
    if (mode === 'none') {
      setIsForwardingEnabled(false);
      if (isModal && setIsForwardingEnabledForModal) {
        setIsForwardingEnabledForModal(false);
      }
    } else {
      setIsForwardingEnabled(true);
      setForwardingType(mode as ForwardingType);
      if (isModal && setIsForwardingEnabledForModal) {
        setIsForwardingEnabledForModal(true);
      }
    }
  };
  const isForwardingUpdateRequestPending = getIsRequestPending(
    updatePhoneNumberSettingsRequestStatus,
  );

  // Convert string phone number to PhoneNumberOption format for SmartSearchTeamInput
  const getPhoneNumberOption = useCallback((phoneNumber: string | null): {
    value: string;
    label: string;
    phoneNumber: string;
    teamMember: null;
    id: number;
  } | null => {
    if (!phoneNumber) return null;
    return {
      value: phoneNumber,
      label: phoneNumber,
      phoneNumber,
      teamMember: null,
      id: 0,
    };
  }, []);

  // useEffect hooks to set the state variables based on the settings map
  useEffect(() => {
    setIsForwardingEnabled(forwardingEnabled === 'true');
  }, [forwardingEnabled]);

  useEffect(() => {
    if (forwardingAlwaysEnabled === 'true') {
      setForwardingType('always');
    } else if (
      forwardingUnansweredEnabled === 'false' &&
      forwardingBusyEnabled === 'false' &&
      forwardingOutOfOfficeEnabled === 'false'
    ) {
      setForwardingType('always');
    } else {
      setForwardingType('conditional');
    }
  }, [forwardingAlwaysEnabled]);

  useEffect(() => {
    setIsUnansweredEnabled(forwardingUnansweredEnabled === 'true');
    setIsBusyEnabled(forwardingBusyEnabled === 'true');
    setIsOutOfOfficeEnabled(forwardingOutOfOfficeEnabled === 'true');
  }, [
    forwardingUnansweredEnabled,
    forwardingBusyEnabled,
    forwardingOutOfOfficeEnabled,
  ]);

  useEffect(() => {
    if (forwardingUnansweredSeconds) {
      setUnansweredAfterSeconds(
        forwardingUnansweredSeconds === '' ? '20' : forwardingUnansweredSeconds,
      );
    }
  }, [forwardingUnansweredSeconds]);

  useEffect(() => {
    setForwardingNumber({
      always: forwardingAlwaysNumber,
      unanswered: forwardingUnansweredNumber,
      busy: forwardingBusyNumber,
      ooo: forwardingOutOfOfficeNumber,
    });
  }, [
    forwardingAlwaysNumber,
    forwardingUnansweredNumber,
    forwardingBusyNumber,
    forwardingOutOfOfficeNumber,
  ]);

  useEffect(() => {
    if (forwardingOOOStart && forwardingOOOEnd) {
      const startHour = forwardingOOOStart.split(':')[0];
      const startMinute = forwardingOOOStart.split(':')[1];
      const endHour = forwardingOOOEnd.split(':')[0];
      const endMinute = forwardingOOOEnd.split(':')[1];
      setOutOfOfficeHours({
        start: { hour: startHour, minute: startMinute },
        end: { hour: endHour, minute: endMinute },
      });
    }
  }, [forwardingOOOStart, forwardingOOOEnd]);

  const prevForwardingTypeRef = useRef<ForwardingType | undefined>(undefined);

  useEffect(() => {
    if (prevForwardingTypeRef.current === undefined) {
      prevForwardingTypeRef.current = forwardingType;
      return;
    }
    if (prevForwardingTypeRef.current === forwardingType) {
      return;
    }
    prevForwardingTypeRef.current = forwardingType;

    if (forwardingType === 'always') {
      setIsUnansweredEnabled(false);
      setIsBusyEnabled(false);
      setIsOutOfOfficeEnabled(false);
      setOutOfOfficeHours({
        start: { hour: '', minute: '' },
        end: { hour: '', minute: '' },
      });
      setOooTimeValid(true);
      // Keep conditional forwarding numbers in state when switching modes so they
      // reappear if the user switches back; same-number errors hide while inputs are disabled.
    }
  }, [forwardingType]);

  const onInputSlotFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  const handleTimeChange = (
    field: 'start' | 'end',
    subField: 'hour' | 'minute',
    value: string,
    nextRef?: RefObject<HTMLInputElement>,
  ) => {
    setOutOfOfficeHours((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subField]: value,
      },
    }));

    if (nextRef?.current && value.toString().trim().length === 2) {
      nextRef.current.focus();
    }
  };

  // Validate 24-hour format time: hours [1, 23], minutes [0, 59]
  const validateTimeRange = useCallback(
    (timeRange: TimeRangeValue): boolean => {
      const startHour = Number(timeRange.start.hour);
      const startMinute = Number(timeRange.start.minute);
      const endHour = Number(timeRange.end.hour);
      const endMinute = Number(timeRange.end.minute);

      // Check if all values are valid numbers
      if (
        Number.isNaN(startHour) ||
        Number.isNaN(startMinute) ||
        Number.isNaN(endHour) ||
        Number.isNaN(endMinute)
      ) {
        return false;
      }

      // Validate hours: [1, 23]
      const isStartHourValid = startHour >= 0 && startHour <= 23;
      const isEndHourValid = endHour >= 0 && endHour <= 23;

      // Validate minutes: [0, 59]
      const isStartMinuteValid = startMinute >= 0 && startMinute <= 59;
      const isEndMinuteValid = endMinute >= 0 && endMinute <= 59;

      // Check if start time and end time are equal
      if (startHour === endHour && startMinute === endMinute) {
        return false;
      }

      return (
        isStartHourValid &&
        isStartMinuteValid &&
        isEndHourValid &&
        isEndMinuteValid
      );
    },
    [],
  );

  const handleTimeBlur = useCallback(() => {
    const isValid = validateTimeRange(outOfOfficeHours);
    setOooTimeValid(isValid);
  }, [outOfOfficeHours, validateTimeRange]);

  /** True when any visible "Forwarding to same number not allowed" error would show. */
  const hasSameNumberForwardingError = useMemo(() => {
    const linePhone = activeNumberSettings?.phoneNumber;
    if (!linePhone) return false;

    if (
      isForwardingEnabled &&
      forwardingType === 'always' &&
      linePhone === forwardingNumber?.always
    ) {
      return true;
    }

    if (!isForwardingEnabled || forwardingType !== 'conditional') {
      return false;
    }

    if (isUnansweredEnabled && linePhone === forwardingNumber?.unanswered) {
      return true;
    }
    if (isBusyEnabled && linePhone === forwardingNumber?.busy) {
      return true;
    }
    if (isOutOfOfficeEnabled && linePhone === forwardingNumber?.ooo) {
      return true;
    }

    return false;
  }, [
    activeNumberSettings?.phoneNumber,
    forwardingNumber,
    forwardingType,
    isForwardingEnabled,
    isUnansweredEnabled,
    isBusyEnabled,
    isOutOfOfficeEnabled,
  ]);

  const isForwardingConfigurationValid = useMemo(() => {
    if (!isForwardingEnabled) {
      return true;
    }
    if (hasSameNumberForwardingError) {
      return false;
    }
    if (forwardingType === 'always') {
      if (!forwardingNumber?.always) {
        return false;
      }
      if (!isValidForwardingNumber(forwardingNumber.always)) {
        return false;
      }
      return true;
    }
    if (
      forwardingType === 'conditional' &&
      (isUnansweredEnabled || isBusyEnabled || isOutOfOfficeEnabled)
    ) {
      if (
        isUnansweredEnabled &&
        (!unansweredAfterSeconds || !forwardingNumber?.unanswered)
      ) {
        return false;
      }
      if (
        isUnansweredEnabled &&
        forwardingNumber?.unanswered &&
        !isValidForwardingNumber(forwardingNumber.unanswered)
      ) {
        return false;
      }
      if (isBusyEnabled && !forwardingNumber?.busy) {
        return false;
      }
      if (
        isBusyEnabled &&
        forwardingNumber?.busy &&
        !isValidForwardingNumber(forwardingNumber.busy)
      ) {
        return false;
      }
      if (
        isOutOfOfficeEnabled &&
        (!validateTimeRange(outOfOfficeHours) || !forwardingNumber?.ooo)
      ) {
        return false;
      }
      if (
        isOutOfOfficeEnabled &&
        forwardingNumber?.ooo &&
        !isValidForwardingNumber(forwardingNumber.ooo)
      ) {
        return false;
      }
      return true;
    }
    if (
      forwardingType === 'conditional' &&
      !isUnansweredEnabled &&
      !isBusyEnabled &&
      !isOutOfOfficeEnabled
    ) {
      return false;
    }
    return true;
  }, [
    isForwardingEnabled,
    hasSameNumberForwardingError,
    forwardingType,
    isUnansweredEnabled,
    isBusyEnabled,
    isOutOfOfficeEnabled,
    unansweredAfterSeconds,
    outOfOfficeHours,
    forwardingNumber,
    validateTimeRange,
    isValidForwardingNumber,
  ]);

  const getSettingsToApply = (
    settings: Array<{ code: string; value: string | null }>,
  ) => {
    const conditionalModeActive =
      isForwardingEnabled && forwardingType === 'conditional';

    const unansweredActive = conditionalModeActive && isUnansweredEnabled;
    const busyActive = conditionalModeActive && isBusyEnabled;
    const oooActive = conditionalModeActive && isOutOfOfficeEnabled;

    // Always add forwarding enabled
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_ENABLED,
      value: isForwardingEnabled ? 'true' : 'false',
    });

    // Always forwarding mode settings
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_ALWAYS_ENABLED,
      value: forwardingType === 'always' ? 'true' : 'false',
    });
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_ALWAYS_NUMBER,
      value:
        isForwardingEnabled && forwardingType === 'always'
          ? forwardingNumber.always || null
          : forwardingAlwaysNumber || null,
    });

    // Unanswered forwarding - only when conditional mode is active
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_UNANSWERED_ENABLED,
      value: unansweredActive ? 'true' : 'false',
    });
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_UNANSWERED_SECONDS,
      value: unansweredActive ? unansweredAfterSeconds || '20' : '20',
    });
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_UNANSWERED_NUMBER,
      value: unansweredActive
        ? forwardingNumber.unanswered || null
        : forwardingUnansweredNumber || null,
    });

    // Busy forwarding
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_BUSY_ENABLED,
      value: busyActive ? 'true' : 'false',
    });
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_BUSY_NUMBER,
      value: busyActive
        ? forwardingNumber.busy || null
        : forwardingBusyNumber || null,
    });

    // Out of office forwarding
    settings.push({
      code: PhoneNumberSettingCode.FORWARDING_OOF_ENABLED,
      value: oooActive ? 'true' : 'false',
    });

    if (!oooActive) {
      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_START,
        value: null,
      });
      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_END,
        value: null,
      });
      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_NUMBER,
        value: forwardingOutOfOfficeNumber || null,
      });
    } else {
      // Format time as "HH:MM" or null if any component is invalid
      const isValidTimeComponent = (value: number | string): boolean => {
        const valueStr = String(value || '').trim();
        return valueStr !== '';
      };

      const isStartHourValid = isValidTimeComponent(
        outOfOfficeHours.start.hour,
      );
      const isStartMinuteValid = isValidTimeComponent(
        outOfOfficeHours.start.minute,
      );
      const isEndHourValid = isValidTimeComponent(outOfOfficeHours.end.hour);
      const isEndMinuteValid = isValidTimeComponent(
        outOfOfficeHours.end.minute,
      );

      // If any of the four components is invalid, set both times to null
      const isAnyComponentInvalid =
        !isStartHourValid ||
        !isStartMinuteValid ||
        !isEndHourValid ||
        !isEndMinuteValid;

      const startTime = isAnyComponentInvalid
        ? null
        : `${outOfOfficeHours.start.hour}:${outOfOfficeHours.start.minute}`;
      const endTime = isAnyComponentInvalid
        ? null
        : `${outOfOfficeHours.end.hour}:${outOfOfficeHours.end.minute}`;

      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_START,
        value: startTime,
      });
      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_END,
        value: endTime,
      });
      settings.push({
        code: PhoneNumberSettingCode.FORWARDING_OOF_NUMBER,
        value: forwardingNumber.ooo || null,
      });
    }
  };
  // Calculate initial forwarding type from settings
  const initialForwardingType = useMemo((): ForwardingType => {
    if (forwardingAlwaysEnabled === 'true') {
      return 'always';
    } else if (
      forwardingUnansweredEnabled === 'false' &&
      forwardingBusyEnabled === 'false' &&
      forwardingOutOfOfficeEnabled === 'false'
    ) {
      return 'always';
    } else {
      return 'conditional';
    }
  }, [
    forwardingAlwaysEnabled,
    forwardingUnansweredEnabled,
    forwardingBusyEnabled,
    forwardingOutOfOfficeEnabled,
  ]);

  // Calculate initial OOO hours from settings
  const initialOutOfOfficeHours = useMemo((): TimeRangeValue => {
    if (forwardingOOOStart && forwardingOOOEnd) {
      const startHour = forwardingOOOStart.split(':')[0];
      const startMinute = forwardingOOOStart.split(':')[1];
      const endHour = forwardingOOOEnd.split(':')[0];
      const endMinute = forwardingOOOEnd.split(':')[1];
      return {
        start: { hour: startHour, minute: startMinute },
        end: { hour: endHour, minute: endMinute },
      };
    }
    return {
      start: { hour: '', minute: '' },
      end: { hour: '', minute: '' },
    };
  }, [forwardingOOOStart, forwardingOOOEnd]);

  // Check if any settings have been changed (dirty state)
  const isDirty = useMemo(() => {
    // Compare forwarding enabled
    if (isForwardingEnabled !== (forwardingEnabled === 'true')) {
      return true;
    }

    // Compare forwarding type
    if (forwardingType !== initialForwardingType) {
      return true;
    }

    // Compare unanswered enabled
    if (isUnansweredEnabled !== (forwardingUnansweredEnabled === 'true')) {
      return true;
    }

    // Compare busy enabled
    if (isBusyEnabled !== (forwardingBusyEnabled === 'true')) {
      return true;
    }

    // Compare out of office enabled
    if (isOutOfOfficeEnabled !== (forwardingOutOfOfficeEnabled === 'true')) {
      return true;
    }

    // Compare unanswered seconds
    const initialUnansweredSeconds = forwardingUnansweredSeconds || '20';
    if (unansweredAfterSeconds !== initialUnansweredSeconds) {
      return true;
    }

    // Compare forwarding numbers
    if (forwardingNumber.always !== forwardingAlwaysNumber) {
      return true;
    }
    if (forwardingNumber.unanswered !== forwardingUnansweredNumber) {
      return true;
    }
    if (forwardingNumber.busy !== forwardingBusyNumber) {
      return true;
    }
    if (forwardingNumber.ooo !== forwardingOutOfOfficeNumber) {
      return true;
    }

    // Compare OOO hours
    if (
      outOfOfficeHours.start.hour !== initialOutOfOfficeHours.start.hour ||
      outOfOfficeHours.start.minute !== initialOutOfOfficeHours.start.minute ||
      outOfOfficeHours.end.hour !== initialOutOfOfficeHours.end.hour ||
      outOfOfficeHours.end.minute !== initialOutOfOfficeHours.end.minute
    ) {
      return true;
    }

    return false;
  }, [
    isForwardingEnabled,
    forwardingEnabled,
    forwardingType,
    initialForwardingType,
    isUnansweredEnabled,
    forwardingUnansweredEnabled,
    isBusyEnabled,
    forwardingBusyEnabled,
    isOutOfOfficeEnabled,
    forwardingOutOfOfficeEnabled,
    unansweredAfterSeconds,
    forwardingUnansweredSeconds,
    forwardingNumber,
    forwardingAlwaysNumber,
    forwardingUnansweredNumber,
    forwardingBusyNumber,
    forwardingOutOfOfficeNumber,
    outOfOfficeHours,
    initialOutOfOfficeHours,
  ]);

  const isSaveButtonDisabled = useMemo(() => {
    // If nothing has changed, disable save button
    if (!isDirty) {
      return true;
    }

    if (errors) {
      return true;
    }

    if (isForwardingEnabled) {
      return (
        !isForwardingConfigurationValid || isForwardingUpdateRequestPending
      );
    }
    return isForwardingUpdateRequestPending;
  }, [
    isDirty,
    isForwardingConfigurationValid,
    updatePhoneNumberSettingsRequestStatus,
    isForwardingEnabled,
    errors,
  ]);

  const handleSaveForwardingConfiguration = useCallback(() => {
    if (isSaveButtonDisabled) {
      return;
    }

    const settings: Array<{ code: string; value: string | null }> = [];
    getSettingsToApply(settings);
    onSaveForwardingConfiguration(settings);
  }, [
    isForwardingConfigurationValid,
    activeNumberId,
    isForwardingEnabled,
    forwardingType,
    forwardingNumber,
    isUnansweredEnabled,
    unansweredAfterSeconds,
    isBusyEnabled,
    isOutOfOfficeEnabled,
    outOfOfficeHours,
    onSaveForwardingConfiguration,
    isSaveButtonDisabled,
  ]);

  // Pass the forwarding configuration validity to the parent component(Modal)
  useEffect(() => {
    if (isModal && passIsForwardingConfigurationValid) {
      passIsForwardingConfigurationValid(isForwardingConfigurationValid);
    }
    if (
      isModal &&
      isForwardingConfigurationValid &&
      setSettingsToApplyForModal
    ) {
      const settings: Array<{ code: string; value: string | null }> = [];
      getSettingsToApply(settings);
      setSettingsToApplyForModal(settings);
    }
  }, [
    isForwardingEnabled,
    forwardingType,
    isUnansweredEnabled,
    isBusyEnabled,
    isOutOfOfficeEnabled,
    unansweredAfterSeconds,
    outOfOfficeHours,
    forwardingNumber,
    validateTimeRange,
  ]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getPhoneNumberDetailsRequestStatus,
      onSuccess: () => {
        resetGetPhoneNumberDetailsRequest();
      },
    });
  }, [getPhoneNumberDetailsRequestStatus, resetGetPhoneNumberDetailsRequest]);

  useEffect(() => {
    if (!isUnansweredEnabled) {
      setErrors(null);
    }
  }, [isUnansweredEnabled]);

  return (
    <div className="call-forwarding-settings-content">
      <div className="call-forwarding-settings-content__forward-to">
        <RadioButtonGroup
          name="forwarding-mode"
          value={forwardingMode}
          onChange={(value) => handleForwardingModeChange(value as ForwardingMode)}
          options={[{ label: 'Do not forward call', value: 'none' }]}
        />
        <div
          className={`always-forward-the-calls ${
            !isForwardingEnabled ? 'always-forward-the-calls-disabled' : ''
          }`}
        >
          <RadioButtonGroup
            name="forwarding-mode"
            value={forwardingMode}
            onChange={(value) => handleForwardingModeChange(value as ForwardingMode)}
            options={[{ label: 'Always Forward the Calls', value: 'always' }]}
          />
          <div className="forwarding-number-input-group">
            <PhoneNumberSearchInputV2
              className="smart-search-team-input"
              loadOptions={loadPhoneNumberOptions}
              cacheRef={memberPhoneNumbersCacheRef}
              disabled={!isForwardingEnabled || isConditionalForwardingEnabled}
              value={getPhoneNumberOption(forwardingNumber.always)}
              onChange={(option) => {
                setForwardingNumber((prev) => ({
                  ...prev,
                  always: option?.phoneNumber || option?.value || null,
                }));
              }}
              errorMessage={getForwardingPhoneInputErrorMessage({
                isSameAsLineNumber:
                  activeNumberSettings?.phoneNumber ===
                  forwardingNumber?.always,
                isFieldActive:
                  isForwardingEnabled && !isConditionalForwardingEnabled,
                value: forwardingNumber?.always,
              })}
            />
            {(() => {
              const isDisabled =
                !isForwardingEnabled ||
                isConditionalForwardingEnabled ||
                !isValidForwardingNumber(forwardingNumber.always);
              return (
                <span
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  className={`test-number-link${
                    isDisabled ? ' test-number-link--disabled' : ''
                  }`}
                  onClick={buildTestLinkHandler(
                    forwardingNumber.always,
                    isDisabled,
                  )}
                  onKeyDown={buildTestLinkKeyDownHandler(
                    forwardingNumber.always,
                    isDisabled,
                  )}
                >
                  Test number
                </span>
              );
            })()}
          </div>
        </div>
        <div className="forward-calls-based-on-conditions">
          <RadioButtonGroup
            name="forwarding-mode"
            value={forwardingMode}
            onChange={(value) => handleForwardingModeChange(value as ForwardingMode)}
            options={[{ label: 'Forward calls based on conditions', value: 'conditional' }]}
          />
          <div className="forward-calls-based-on-conditions__content">
            {/* When unanswered */}
            <div
              className={`when-unanswered ${
                isConditionalForwardingEnabled && isForwardingEnabled
                  ? ''
                  : 'when-unanswered_disabled'
              }`}
            >
              <Switch
                checked={isUnansweredEnabled}
                onChange={(checked) => setIsUnansweredEnabled(checked)}
                size={SwitchSize.Small}
                disabled={
                  !isConditionalForwardingEnabled || !isForwardingEnabled
                }
              />
              <span className="forwarding-condition-label ">
                Forward when unanswered after
              </span>
              <div className="unanswered-seconds-container">
                <div
                  className={`unanswered-seconds-input ${
                    errors ? 'error' : ''
                  }`}
                >
                  <Input
                    type="text"
                    disabled={!isUnansweredEnabled}
                    value={unansweredAfterSeconds}
                    onChange={(value) => {
                      // Filter out non-numeric characters
                      const onlyNumbers = value?.replace(/[^0-9]/g, '') || '';
                      // Limit to max 2 characters
                      const limitedValue = onlyNumbers.slice(0, 2);
                      setUnansweredAfterSeconds(limitedValue);
                    }}
                    onBlur={() => {
                      // Ensure value is clamped on blur if empty or invalid
                      if (
                        unansweredAfterSeconds === '' ||
                        unansweredAfterSeconds === null
                      ) {
                        setUnansweredAfterSeconds('10');
                      } else {
                        const numValue = Number(unansweredAfterSeconds);
                        if (numValue < 10) {
                          setErrors('Seconds must be greater than 10');
                        } else if (numValue > 60) {
                          setErrors('Seconds must be less than 60');
                        } else {
                          setErrors(null);
                        }
                      }
                    }}
                  />
                  <span className="seconds-label">secs</span>
                </div>
                {isUnansweredEnabled && errors && (
                  <span className="error-message">{errors}</span>
                )}
              </div>
              <div className="forwarding-number-input-group">
                <PhoneNumberSearchInputV2
                  className="smart-search-team-input"
                  loadOptions={loadPhoneNumberOptions}
                  cacheRef={memberPhoneNumbersCacheRef}
                  disabled={
                    !isUnansweredEnabled ||
                    forwardingType === 'always' ||
                    !isForwardingEnabled
                  }
                  value={getPhoneNumberOption(forwardingNumber.unanswered)}
                  onChange={(option) => {
                    setForwardingNumber((prev) => ({
                      ...prev,
                      unanswered: option?.phoneNumber || option?.value || null,
                    }));
                  }}
                  errorMessage={getForwardingPhoneInputErrorMessage({
                    isSameAsLineNumber:
                      activeNumberSettings?.phoneNumber ===
                      forwardingNumber?.unanswered,
                    isFieldActive:
                      isUnansweredEnabled &&
                      isConditionalForwardingEnabled &&
                      isForwardingEnabled,
                    value: forwardingNumber?.unanswered,
                  })}
                />
                {(() => {
                  const isDisabled =
                    !isForwardingEnabled ||
                    !isUnansweredEnabled ||
                    forwardingType === 'always' ||
                    !isValidForwardingNumber(forwardingNumber.unanswered);
                  return (
                    <span
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      className={`test-number-link${
                        isDisabled ? ' test-number-link--disabled' : ''
                      }`}
                      onClick={buildTestLinkHandler(
                        forwardingNumber.unanswered,
                        isDisabled,
                      )}
                      onKeyDown={buildTestLinkKeyDownHandler(
                        forwardingNumber.unanswered,
                        isDisabled,
                      )}
                    >
                      Test number
                    </span>
                  );
                })()}
              </div>
            </div>
            {/* When busy */}
            <div
              className={`when-busy ${
                isConditionalForwardingEnabled && isForwardingEnabled
                  ? ''
                  : 'when-busy_disabled'
              }`}
            >
              <Switch
                checked={isBusyEnabled}
                onChange={(checked) => setIsBusyEnabled(checked)}
                size={SwitchSize.Small}
                disabled={
                  !isConditionalForwardingEnabled || !isForwardingEnabled
                }
              />
              <span className="forwarding-condition-label">
                Forward when busy
              </span>
              <div className="forwarding-number-input-group">
                <PhoneNumberSearchInputV2
                  className="smart-search-team-input"
                  loadOptions={loadPhoneNumberOptions}
                  cacheRef={memberPhoneNumbersCacheRef}
                  disabled={
                    !isBusyEnabled ||
                    forwardingType === 'always' ||
                    !isForwardingEnabled
                  }
                  value={getPhoneNumberOption(forwardingNumber.busy)}
                  onChange={(option) => {
                    setForwardingNumber((prev) => ({
                      ...prev,
                      busy: option?.phoneNumber || option?.value || null,
                    }));
                  }}
                  errorMessage={getForwardingPhoneInputErrorMessage({
                    isSameAsLineNumber:
                      activeNumberSettings?.phoneNumber ===
                      forwardingNumber?.busy,
                    isFieldActive:
                      isBusyEnabled &&
                      isConditionalForwardingEnabled &&
                      isForwardingEnabled,
                    value: forwardingNumber?.busy,
                  })}
                />
                {(() => {
                  const isDisabled =
                    !isForwardingEnabled ||
                    !isBusyEnabled ||
                    forwardingType === 'always' ||
                    !isValidForwardingNumber(forwardingNumber.busy);
                  return (
                    <span
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      className={`test-number-link${
                        isDisabled ? ' test-number-link--disabled' : ''
                      }`}
                      onClick={buildTestLinkHandler(
                        forwardingNumber.busy,
                        isDisabled,
                      )}
                      onKeyDown={buildTestLinkKeyDownHandler(
                        forwardingNumber.busy,
                        isDisabled,
                      )}
                    >
                      Test number
                    </span>
                  );
                })()}
              </div>
            </div>
            {/* Out of office hours */}
            <div
              className={`when-ooo ${
                isConditionalForwardingEnabled && isForwardingEnabled
                  ? ''
                  : 'when-ooo_disabled'
              }`}
            >
              <Switch
                checked={isOutOfOfficeEnabled}
                onChange={(checked) => setIsOutOfOfficeEnabled(checked)}
                size={SwitchSize.Small}
                disabled={
                  !isConditionalForwardingEnabled || !isForwardingEnabled
                }
              />
              <span className="forwarding-condition-label">
                Forward when out of office hours
              </span>
              <div className="schedule-timeslot-container">
                <div
                  className={`schedule-timeslot active ${
                    oooTimeValid ? '' : 'error'
                  }`}
                >
                  <input
                    type="number"
                    placeholder="hh"
                    value={outOfOfficeHours.start.hour}
                    ref={inputStartHourRef}
                    min="1"
                    max="23"
                    onFocus={onInputSlotFocus}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleTimeChange(
                        'start',
                        'hour',
                        e.target.value,
                        inputStartMinRef,
                      );
                    }}
                    onBlur={handleTimeBlur}
                    disabled={
                      !isOutOfOfficeEnabled ||
                      forwardingType === 'always' ||
                      !isForwardingEnabled
                    }
                  />
                  <small style={{ opacity: !isOutOfOfficeEnabled ? 0.5 : 1 }}>
                    :
                  </small>
                  <input
                    type="number"
                    placeholder="mm"
                    value={outOfOfficeHours.start.minute}
                    ref={inputStartMinRef}
                    min="0"
                    max="59"
                    onFocus={onInputSlotFocus}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleTimeChange(
                        'start',
                        'minute',
                        e.target.value,
                        inputEndHourRef,
                      );
                    }}
                    onBlur={handleTimeBlur}
                    disabled={
                      !isOutOfOfficeEnabled ||
                      forwardingType === 'always' ||
                      !isForwardingEnabled
                    }
                  />
                  <p style={{ opacity: !isOutOfOfficeEnabled ? 0.5 : 1 }}>-</p>
                  <input
                    type="number"
                    placeholder="hh"
                    value={outOfOfficeHours.end.hour}
                    min="1"
                    max="23"
                    onFocus={onInputSlotFocus}
                    ref={inputEndHourRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleTimeChange(
                        'end',
                        'hour',
                        e.target.value,
                        inputEndMinRef,
                      );
                    }}
                    onBlur={handleTimeBlur}
                    disabled={
                      !isOutOfOfficeEnabled ||
                      forwardingType === 'always' ||
                      !isForwardingEnabled
                    }
                  />
                  <small style={{ opacity: !isOutOfOfficeEnabled ? 0.5 : 1 }}>
                    :
                  </small>
                  <input
                    type="number"
                    placeholder="mm"
                    value={outOfOfficeHours.end.minute}
                    min="0"
                    max="59"
                    onFocus={onInputSlotFocus}
                    ref={inputEndMinRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleTimeChange(
                        'end',
                        'minute',
                        e.target.value,
                        inputStartHourRef,
                      );
                    }}
                    onBlur={handleTimeBlur}
                    disabled={
                      !isOutOfOfficeEnabled ||
                      forwardingType === 'always' ||
                      !isForwardingEnabled
                    }
                  />
                </div>
                {!oooTimeValid && isOutOfOfficeEnabled && (
                  <span className="error-message">
                    Please enter a valid time
                  </span>
                )}
              </div>
              <div className="forwarding-number-input-group">
                <PhoneNumberSearchInputV2
                  className="smart-search-team-input"
                  loadOptions={loadPhoneNumberOptions}
                  cacheRef={memberPhoneNumbersCacheRef}
                  disabled={
                    !isOutOfOfficeEnabled ||
                    forwardingType === 'always' ||
                    !isForwardingEnabled
                  }
                  value={getPhoneNumberOption(forwardingNumber.ooo)}
                  onChange={(option) => {
                    setForwardingNumber((prev) => ({
                      ...prev,
                      ooo: option?.phoneNumber || option?.value || null,
                    }));
                  }}
                  errorMessage={getForwardingPhoneInputErrorMessage({
                    isSameAsLineNumber:
                      activeNumberSettings?.phoneNumber ===
                      forwardingNumber?.ooo,
                    isFieldActive:
                      isOutOfOfficeEnabled &&
                      isConditionalForwardingEnabled &&
                      isForwardingEnabled,
                    value: forwardingNumber?.ooo,
                  })}
                />
                {(() => {
                  const isDisabled =
                    !isForwardingEnabled ||
                    !isOutOfOfficeEnabled ||
                    forwardingType === 'always' ||
                    !isValidForwardingNumber(forwardingNumber.ooo);
                  return (
                    <span
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      className={`test-number-link${
                        isDisabled ? ' test-number-link--disabled' : ''
                      }`}
                      onClick={buildTestLinkHandler(
                        forwardingNumber.ooo,
                        isDisabled,
                      )}
                      onKeyDown={buildTestLinkKeyDownHandler(
                        forwardingNumber.ooo,
                        isDisabled,
                      )}
                    >
                      Test number
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isModal && (
        <div className="call-forwarding-settings__action">
          <Button
            variant="primary"
            onClick={handleSaveForwardingConfiguration}
            disabled={isSaveButtonDisabled}
            isLoading={isForwardingUpdateRequestPending}
            loadingText="Save"
            className="call-forwarding-settings__save-button"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export default CallForwardingSettingsContent;
